import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Wallet, Bell, CalendarCheck, User, LogOut, Heart, CalendarDays, Clock } from "lucide-react";
import { Link } from "react-router-dom";
import { api, formatApiError, STATUS_LABELS } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { WalletRitas } from "../components/WalletRitas";

const statusColor = {
  en_attente: "text-yellow-400 border-yellow-400/30",
  confirmee: "text-[#009246] border-[#009246]/40",
  refusee: "text-[#C8102E] border-[#C8102E]/40",
  annulee: "text-gray-400 border-gray-500/40",
};

export default function ProfilePage() {
  const { user, setUser, logout } = useAuth();
  const [tab, setTab] = useState("reservations");
  const [reservations, setReservations] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [form, setForm] = useState({ first_name: "", last_name: "", phone: "", birthday: "" });

  useEffect(() => {
    api.get("/my-reservations").then((r) => setReservations(r.data)).catch(() => {});
    api.get("/notifications").then((r) => setNotifications(r.data)).catch(() => {});
    api.get("/favorites").then((r) => setFavorites(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (user && user !== false) {
      setForm({ first_name: user.first_name || "", last_name: user.last_name || "", phone: user.phone || "", birthday: user.birthday || "" });
    }
  }, [user]);

  const saveProfile = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.put("/auth/profile", form);
      setUser(data);
      toast.success("Profil mis à jour !");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  if (!user || user === false) return null;

  const tabs = [
    { id: "reservations", label: "Mes réservations", icon: CalendarCheck },
    { id: "wallet", label: "Wallet RITAS", icon: Wallet },
    { id: "favoris", label: "Mes favoris", icon: Heart },
    { id: "notifications", label: "Notifications", icon: Bell },
    { id: "infos", label: "Mes informations", icon: User },
  ];

  return (
    <div className="max-w-4xl mx-auto px-6 py-16" data-testid="profile-page">
      <div className="flex items-center justify-between mb-10">
        <div>
          <p className="gold-label mb-2">Espace client</p>
          <h1 className="font-serif-elegant text-4xl font-bold">Bonjour, {user.first_name}</h1>
        </div>
        <button onClick={logout} data-testid="profile-logout-btn" className="btn-outline-gold !px-4 !py-2 text-sm">
          <LogOut size={15} /> Déconnexion
        </button>
      </div>
      <div className="flex gap-2 overflow-x-auto pb-4 mb-8">
        {tabs.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            data-testid={`profile-tab-${id}`}
            onClick={() => setTab(id)}
            className={`whitespace-nowrap flex items-center gap-2 px-5 py-2 rounded-full text-sm border transition-colors duration-200 ${
              tab === id ? "bg-[#D4AF37] text-black border-[#D4AF37] font-medium" : "border-white/15 text-gray-300"
            }`}
          >
            <Icon size={15} /> {label}
          </button>
        ))}
      </div>

      {tab === "reservations" && (
        <div className="space-y-4" data-testid="profile-reservations">
          {reservations.length === 0 && <p className="text-gray-400">Aucune réservation pour le moment.</p>}
          {reservations.map((r) => (
            <div key={r.id} className="ic-card !transform-none p-6 flex flex-wrap items-center justify-between gap-4">
              <div>
                <p className="font-medium">{new Date(r.date + "T00:00:00").toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long", year: "numeric" })} à {r.time}</p>
                <p className="text-sm text-gray-400 mt-1">{r.party_size} personne(s){r.special_request ? ` · ${r.special_request}` : ""}</p>
              </div>
              <span className={`text-xs border rounded-full px-4 py-1.5 ${statusColor[r.status] || "text-gray-400 border-gray-600"}`}>
                {STATUS_LABELS[r.status] || r.status}
              </span>
            </div>
          ))}
        </div>
      )}

      {tab === "wallet" && <WalletRitas />}

      {tab === "favoris" && (
        <div className="space-y-4" data-testid="profile-favorites">
          {favorites.length === 0 && (
            <div className="ic-card !transform-none p-8 text-center">
              <Heart size={36} className="text-[#D4AF37] mx-auto mb-3" />
              <p className="text-gray-400">Aucune soirée favorite pour le moment.</p>
              <Link to="/soirees" data-testid="favorites-browse-link" className="btn-outline-gold !py-2 text-sm mt-4">Découvrir les soirées</Link>
            </div>
          )}
          {favorites.map((e) => (
            <div key={e.id} className="ic-card !transform-none p-5 flex flex-wrap items-center gap-4" data-testid={`favorite-event-${e.id}`}>
              {e.photo_url && <img src={e.photo_url} alt={e.artist_name} className="w-16 h-16 rounded-lg object-cover border border-white/10" />}
              <div className="flex-1 min-w-0">
                <p className="gold-label">{e.event_type}</p>
                <p className="font-medium truncate">{e.title}</p>
                <p className="text-xs text-gray-400 mt-1 flex items-center gap-3">
                  <span className="flex items-center gap-1"><CalendarDays size={12} />{new Date(e.date + "T00:00:00").toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" })}</span>
                  <span className="flex items-center gap-1"><Clock size={12} />{e.time}</span>
                </p>
              </div>
              {e.status === "publie" && (
                <Link to={`/reserver?event=${e.id}`} data-testid={`favorite-reserve-${e.id}`} className="btn-gold !px-4 !py-2 text-xs">Réserver</Link>
              )}
            </div>
          ))}
        </div>
      )}

      {tab === "notifications" && (
        <div className="space-y-4" data-testid="profile-notifications">
          {notifications.length === 0 && <p className="text-gray-400">Aucune notification.</p>}
          {notifications.map((n) => (
            <div key={n.id} className="ic-card !transform-none p-6">
              <div className="flex items-center gap-3">
                <Bell size={16} className="text-[#D4AF37]" />
                <p className="font-medium">{n.title}</p>
              </div>
              <p className="text-sm text-gray-400 mt-2">{n.message}</p>
              <p className="text-xs text-gray-600 mt-2">{new Date(n.created_at).toLocaleDateString("fr-FR")}</p>
            </div>
          ))}
        </div>
      )}

      {tab === "infos" && (
        <form onSubmit={saveProfile} className="ic-card !transform-none p-8 space-y-4 max-w-lg" data-testid="profile-info-form">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="gold-label block mb-2">Prénom</label>
              <input data-testid="profile-firstname" className="ic-input" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} />
            </div>
            <div>
              <label className="gold-label block mb-2">Nom</label>
              <input data-testid="profile-lastname" className="ic-input" value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} />
            </div>
          </div>
          <div>
            <label className="gold-label block mb-2">Téléphone</label>
            <input data-testid="profile-phone" type="tel" className="ic-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
          </div>
          <div>
            <label className="gold-label block mb-2">Date d'anniversaire</label>
            <input data-testid="profile-birthday" type="date" className="ic-input" value={form.birthday} onChange={(e) => setForm({ ...form, birthday: e.target.value })} />
          </div>
          <p className="text-xs text-gray-500">Email : {user.email}</p>
          <button type="submit" data-testid="profile-save-btn" className="btn-gold !py-3">Enregistrer</button>
        </form>
      )}
    </div>
  );
}
